import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import StandardsSection from '../components/StandardsSection';
import {
  FaArrowRight,
  FaBriefcase,
  FaBuilding,
  FaCheckCircle,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhone,
  FaQrcode,
  FaSearch,
  FaShareSquare,
  FaShieldAlt,
  FaHardHat,
  FaCertificate,
  FaGraduationCap,
  FaTools
} from 'react-icons/fa';

import '../styles/home-page.css';

import homeHeroImage from '../pics/Team1.png';
import aboutImage from '../pics/highlift.png';
import galleryImageOne from '../pics/batchlift.png';
import galleryImageTwo from '../pics/Dofcanworkerslift.png';
import galleryImageThree from '../pics/Calibration232.png';
import galleryImageFour from '../pics/workersconnect.png';
import galleryImageFive from '../pics/batchlowlift.png';
import galleryImageSix from '../pics/serviceimg.png';
import galleryImageSeven from '../pics/scrolworker.png';
import galleryImageEight from '../pics/Aboutpageimage12.png';

const API_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function HomePage() {
  const navigate = useNavigate();

  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();

    if (sending) return;

    const form = e.target;
    const formData = new FormData(form);

    const service = `${formData.get('service') || ''}`.trim();

    const payload = {
      name: formData.get('name') || '',
      email: formData.get('email') || '',
      phone: formData.get('phone') || '',
      company: '',
      service,
      subject: 'Quick Enquiry',
      message: formData.get('message') || '',
      honeypot: formData.get('honeypot') || ''
    };

    setSending(true);
    setError(null);
    setSent(false);

    try {
      const response = await axios.post(
        `${API_URL}/contact`,
        payload,
        { timeout: 20000 }
      );

      if (response.data?.success) {
        setSent(true);
        form.reset();
      } else {
        setError(
          response.data?.message ||
          'We could not send your enquiry. Please try again.'
        );
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        'We could not send your enquiry. Please check your connection and try again.'
      );
    } finally {
      setSending(false);
    }
  };

  const features = [
    {
      icon: <FaQrcode />,
      title: 'Scan QR Code',
      desc: 'Use your phone camera to scan the QR code on your certificate.'
    },
    {
      icon: <FaSearch />,
      title: 'Instant Verification',
      desc: 'Our system instantly verifies the certificate authenticity and details.'
    },
    {
      icon: <FaCheckCircle />,
      title: 'View Details',
      desc: 'See your training details, completion date, and validity status.'
    },
    {
      icon: <FaShareSquare />,
      title: 'Share Certificate',
      desc: 'Download or share your verified certificate with employers.'
    }
  ];

  const pageSummaries = [
    {
      title: 'About Us',
      desc: 'Learn who Dofcan Oilserve is, what we stand for, and why safety, training, and certification sit at the center of our work.',
      icon: <FaBuilding />,
      route: '/about'
    },
    {
      title: 'Services',
      desc: 'Explore our training, rigging, inspection, and certification services built for lifting operations and industrial teams.',
      icon: <FaBriefcase />,
      route: '/services'
    },
    {
      title: 'Training & Certification',
      desc: 'Browse our training programmes and equipment certification services, and verify a DOFCAN certificate by ID, email, or QR code.',
      icon: <FaGraduationCap />,
      route: '/training-certification'
    }
  ];

  const highlights = [
    '15+ years of experience in lifting, inspection, and certification support.',
    'Training, verification, company profiles, and media now connected through one platform.',
    'A clearer path for visitors to move from discovery to trust to action.'
  ];

  const galleryImages = [
    { src: galleryImageOne, position: 'center 55%' },
    { src: galleryImageTwo, position: 'center 50%' },
    { src: galleryImageThree, position: 'center 30%' },
    { src: galleryImageFour, position: 'center 50%' },
    { src: galleryImageFive, position: 'center 50%' },
    { src: galleryImageSix, position: 'center top' },
    { src: galleryImageSeven, position: 'center 40%' },
    { src: galleryImageEight, position: 'center 35%' }
  ];

  return (
    <main className="home-page">
      <SEO
        title="Home"
        description="Verify your Dofcan Oilserve training certificate online and explore our crane operations, load testing, pressure testing, calibration and inspection services for the oil and gas industry."
        path="/"
      />

      {/* ================= HERO ================= */}
      <section className="home-hero">
        <div className="home-hero-overlay"></div>

        <div className="home-hero-content">

          <div className="hero-badge">
            <FaShieldAlt />
            <span>SAFETY • TRAINING • CERTIFICATION</span>
          </div>

          {/* Each line sits in its own clipping box so it can rise out from
              underneath on load - see .hero-title-line in home-page.css */}
          <h1>
            <span className="hero-title-line">
              <span className="hero-title-inner">Verify Your</span>
            </span>

            <span className="hero-title-line hero-title-accent">
              <span className="hero-title-inner">Training Certificate</span>
            </span>
          </h1>

          <p className="hero-subtitle">
            <span className="hero-line-inner">
              ...making the Oil and Gas industry a safer place
            </span>
          </p>

          <p className="hero-description">
            Quickly verify the authenticity and validity of your Dofcan
            Oilserve training certificate through our secure verification
            platform.
          </p>

          <div className="hero-actions">
            <button
              className="hero-primary-btn"
              onClick={() => navigate('/verify')}
            >
              Verify Your Certificate
              <FaArrowRight />
            </button>

            <button
              className="hero-secondary-btn"
              onClick={() => navigate('/services')}
            >
              Explore Our Services
            </button>
          </div>

          <div className="hero-trust">
            <div>
              <FaCertificate />
              <span>Certified Training</span>
            </div>

            <div>
              <FaHardHat />
              <span>Safety Focused</span>
            </div>

            <div>
              <FaTools />
              <span>Industrial Expertise</span>
            </div>
          </div>
        </div>

        <div className="hero-image-wrapper">
          <div className="hero-image-frame">
            <img
              src={homeHeroImage}
              alt="Dofcan Oilserve team"
            />
          </div>

          <div className="hero-floating-card">
            <FaCheckCircle />
            <div>
              <strong>Trusted Certification</strong>
              <span>Secure online verification</span>
            </div>
          </div>
        </div>
      </section>


      {/* ================= VERIFICATION PROCESS ================= */}
      <section className="how-it-works">

        <div className="section-heading">
          <span className="section-label">CERTIFICATE VERIFICATION</span>
          <h2>How to Verify Your Certificate</h2>
          <p>
            Verify your Dofcan Oilserve certificate quickly and securely
            using our simple four-step process.
          </p>
        </div>

        <div className="features-grid">
          {features.map((feature, idx) => (
            <article
              key={idx}
              className="feature-card"
            >
              <div className="feature-number">
                0{idx + 1}
              </div>

              <div className="feature-icon">
                {feature.icon}
              </div>

              <h3>{feature.title}</h3>

              <p>{feature.desc}</p>

              {idx < features.length - 1 && (
                <div className="feature-arrow">
                  <FaArrowRight />
                </div>
              )}
            </article>
          ))}
        </div>

        <div className="verification-cta">
          <div>
            <span>READY TO CHECK YOUR CERTIFICATE?</span>
            <h3>Verify it in seconds.</h3>
          </div>

          <button
            onClick={() => navigate('/verify')}
          >
            Start Verification
            <FaArrowRight />
          </button>
        </div>
      </section>


      {/* ================= ABOUT ================= */}
      <section className="about-section">

        <div className="about-image">
          <img
            src={aboutImage}
            style={{ objectPosition: 'center 45%' }}
            alt="Dofcan Oilserve lifting operations"
          />

          <div className="about-experience">
            <strong>15+</strong>
            <span>Years of Experience</span>
          </div>
        </div>

        <div className="about-content">

          <span className="section-label">ABOUT DOFCAN OILSERVE</span>

          <h2>
            Building a Safer
            <span> Lifting Industry</span>
          </h2>

          <p>
            Dofcan Oilserve Limited is a leading provider of training,
            certification, and equipment inspection services in the
            lifting and oil & gas industry.
          </p>

          <p>
            With over 15 years of experience, we have trained thousands
            of professionals and certified equipment across Africa. Our
            commitment to safety and excellence is reflected in every
            training program and certification we issue.
          </p>

          <div className="about-points">
            <div>
              <FaCheckCircle />
              <span>Professional Training</span>
            </div>

            <div>
              <FaCheckCircle />
              <span>Equipment Inspection</span>
            </div>

            <div>
              <FaCheckCircle />
              <span>Certificate Verification</span>
            </div>

            <div>
              <FaCheckCircle />
              <span>Safety-Focused Operations</span>
            </div>
          </div>

          <div className="about-buttons">
            <button
              className="primary-btn"
              onClick={() => navigate('/about')}
            >
              Learn More
              <FaArrowRight />
            </button>

            <button
              className="outline-btn"
              onClick={() => navigate('/services')}
            >
              View Services
            </button>
          </div>

        </div>
      </section>


      {/* ================= MANAGEMENT STANDARDS ================= */}
      <StandardsSection
        eyebrow="Our Standards"
        title="Quality, Environment & Safety"
        intro="Our operational approach is built around three internationally recognised management standards, applied across our training, inspection, and certification work."
        note="Dofcan Oilserve applies the principles of these management standards across our operations."
      />


      {/* ================= SITE SECTIONS ================= */}
      <section className="summary-section">

        <div className="section-heading">
          <span className="section-label">EXPLORE DOFCAN</span>

          <h2>Everything You Need In One Place</h2>

          <p>
            Explore our company, services, partner organizations and
            certificate verification platform.
          </p>
        </div>

        <div className="summary-grid">

          {pageSummaries.map((item, index) => (
            <article
              key={item.title}
              className="summary-card"
            >
              <div className="summary-top">
                <span>0{index + 1}</span>

                <div className="summary-icon">
                  {item.icon}
                </div>
              </div>

              <h3>{item.title}</h3>

              <p>{item.desc}</p>

              <button
                className="summary-link"
                onClick={() => navigate(item.route)}
              >
                Open Section
                <FaArrowRight />
              </button>
            </article>
          ))}

        </div>
      </section>


      {/* ================= SNAPSHOT ================= */}
      <section className="snapshot-section">

        <div className="snapshot-content">

          <div className="snapshot-intro">
            <span className="section-label">WHY DOFCAN</span>

            <h2>
              Experience,
              <span> Safety & Trust</span>
            </h2>

            <p>
              This platform brings together certificate verification,
              company presence, service information, contact pathways,
              and a visual gallery in one flow.
            </p>
          </div>

          <div className="snapshot-list">

            {highlights.map((item, index) => (
              <div
                key={item}
                className="snapshot-item"
              >
                <div className="snapshot-icon">
                  <FaCheckCircle />
                </div>

                <div>
                  <span className="snapshot-number">
                    0{index + 1}
                  </span>

                  <p>{item}</p>
                </div>
              </div>
            ))}

          </div>

        </div>
      </section>


      {/* ================= GALLERY ================= */}
      <section className="gallery-section">

        <div className="gallery-header-row">

          <div>
            <span className="section-label">OUR WORK</span>

            <h2>Our Work & Services</h2>

            <p>
              Preview some of the field images that now power the
              dedicated gallery page.
            </p>
          </div>

          <button
            className="gallery-btn"
            onClick={() => navigate('/gallery')}
          >
            View Full Gallery
            <FaArrowRight />
          </button>

        </div>

        <div className="gallery-grid">

          {galleryImages.map(({ src, position }, i) => (
            <div
              key={i}
              className={`gallery-item gallery-item-${i + 1}`}
            >
              <img
                src={src}
                style={{ objectPosition: position }}
                alt={`Dofcan service activity ${i + 1}`}
              />

              <div className="gallery-overlay">
                <span>DOFCAN OILSERVE</span>
                <strong>Industrial Operations</strong>
              </div>
            </div>
          ))}

        </div>

      </section>


      {/* ================= VERIFY CTA ================= */}
      <section className="home-verify-section">

        <div className="verify-background-shape"></div>

        <div className="verify-content">

          <div className="verify-icon">
            <FaCertificate />
          </div>

          <span className="section-label">
            CERTIFICATE VERIFICATION
          </span>

          <h2>Verify Your Certificate Now</h2>

          <p>
            Have a training certificate from Dofcan? Scan the QR code
            or enter your details below.
          </p>

          <button
            className="verify-main-btn"
            onClick={() => navigate('/verify')}
          >
            Start Verification
            <FaArrowRight />
          </button>

        </div>
      </section>


      {/* ================= CONTACT ================= */}
      <section className="home-contact-section">

        <div className="home-contact-header">

          <span className="section-label">GET IN TOUCH</span>

          <h2>Contact Us</h2>

          <p>
            This is now the closing segment of the home page so visitors
            can move directly from browsing the site into sending an
            enquiry.
          </p>

        </div>

        <div className="home-contact-layout">

          <div className="home-contact-info">

            <article className="home-contact-card">
              <div className="home-contact-icon">
                <FaEnvelope />
              </div>

              <div>
                <span>CONTACT</span>
                <h3>Email</h3>
                <a href="mailto:dofcanoilserve@yahoo.com">
                  dofcanoilserve@yahoo.com
                </a>
              </div>
            </article>


            <article className="home-contact-card">
              <div className="home-contact-icon">
                <FaPhone />
              </div>

              <div>
                <span>CALL US</span>
                <h3>Phone</h3>
                <a href="tel:+2348036726334">
                  +234 (0) 803 672 6334
                </a>
              </div>
            </article>


            <article className="home-contact-card">
              <div className="home-contact-icon">
                <FaMapMarkerAlt />
              </div>

              <div>
                <span>OUR LOCATION</span>
                <h3>Location</h3>
                <p>Lagos, Nigeria</p>
              </div>
            </article>

          </div>


          <form
            className="home-contact-form"
            onSubmit={handleEnquirySubmit}
          >

            <div className="home-contact-form-header">

              <span>QUICK ENQUIRY</span>

              <h3>Send An Enquiry</h3>

              <p>
                Tell us what you need and we’ll guide you to the
                right service quickly.
              </p>

            </div>


            <div className="home-contact-form-grid">

              <label className="home-contact-field">
                <span>Full Name</span>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="Enter your full name"
                />
              </label>


              <label className="home-contact-field">
                <span>Email Address</span>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="Enter your email address"
                />
              </label>


              <label className="home-contact-field">
                <span>Phone Number</span>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Enter your phone number"
                />
              </label>


              <label className="home-contact-field">
                <span>Service Needed</span>
                <input
                  type="text"
                  name="service"
                  placeholder="Training, inspection, verification..."
                />
              </label>


              <label className="home-contact-field home-contact-field-full">
                <span>Message</span>
                <textarea
                  rows="5"
                  name="message"
                  required
                  placeholder="Tell us about your request"
                />
              </label>

            </div>


            {/* Spam trap: hidden from real visitors */}
            <input
              type="text"
              name="honeypot"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ display: 'none' }}
            />


            {error && (
              <p className="home-contact-error" role="alert">
                {error}
              </p>
            )}

            {sent && !error && (
              <p className="home-contact-sent" role="status">
                <FaCheckCircle />
                Thank you — your enquiry has been sent. Our team will
                respond to you shortly.
              </p>
            )}


            <div className="home-contact-actions">

              <button
                type="submit"
                className="hero-btn"
                disabled={sending}
              >
                {sending ? 'Sending...' : 'Send Enquiry'}
                <FaArrowRight />
              </button>

              <button
                type="button"
                className="home-verify-btn"
                onClick={() => navigate('/verify')}
              >
                Go To Verification
              </button>

            </div>

          </form>

        </div>

      </section>

    </main>
  );
}